import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SplashScreen } from "@/components/SplashScreen";
import OfflinePendingBar from "@/components/OfflinePendingBar";
import { startOfflineSync } from "@/lib/offlineSales";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Activate from "@/pages/Activate";

import AdminLayout from "@/layouts/AdminLayout";
import AdminHome from "@/pages/admin/AdminHome";
import AdminClients from "@/pages/admin/AdminClients";

import DashboardLayout from "@/layouts/DashboardLayout";
import Overview from "@/pages/client/Overview";
import OpenOrders from "@/pages/client/OpenOrders";
import Stats from "@/pages/client/Stats";
import Products from "@/pages/client/Products";
import Stores from "@/pages/client/Stores";
import Printers from "@/pages/client/Printers";
import Tables from "@/pages/client/Tables";
import Closures from "@/pages/client/Closures";
import Warehouse from "@/pages/client/Warehouse";
import Staff from "@/pages/client/Staff";
import Invoicing from "@/pages/client/Invoicing";
import Exports from "@/pages/client/Exports";
import Branding from "@/pages/client/Branding";
import ReportOperators from "@/pages/client/ReportOperators";
import Refunds from "@/pages/client/Refunds";
import Loyalty from "@/pages/client/Loyalty";
import Billing from "@/pages/client/Billing";
import GestionaleReport from "@/pages/client/GestionaleReport";
import DocumentsDay from "@/pages/client/DocumentsDay";
import SalesAnalytics from "@/pages/client/SalesAnalytics";

import ModeSelector from "@/pages/pos/ModeSelector";
import POSRestaurant from "@/pages/pos/POSRestaurant";
import POSFesta from "@/pages/pos/POSFesta";
import POSNegozio from "@/pages/pos/POSNegozio";
import POSLavanderia from "@/pages/pos/POSLavanderia";
import POSPizzeria from "@/pages/pos/POSPizzeria";
import POSParrucchiere from "@/pages/pos/POSParrucchiere";
import POSPesati from "@/pages/pos/POSPesati";
import POSRapido from "@/pages/pos/POSRapido";
import POSGelateria from "@/pages/pos/POSGelateria";
import POSAutolavaggio from "@/pages/pos/POSAutolavaggio";
import POSBnb from "@/pages/pos/POSBnb";
import POSToelettatura from "@/pages/pos/POSToelettatura";
import Palmare from "@/pages/pos/Palmare";
import PublicLoyaltyCard from "@/pages/public/PublicLoyaltyCard";
import { FeaturesProvider, RequireMode, RequireFeature } from "@/lib/features";
import UpdateBanner from "@/components/UpdateBanner";
import WhatsNew from "@/components/WhatsNew";
import DemoBadge from "@/components/DemoBadge";
import { StoreProvider } from "@/lib/storeContext";
import AutoAppUpdate from "@/components/AutoAppUpdate";
import { getDeviceRole } from "@/lib/deviceRole";
import { isVeltaApk, staffHomePath, staffCanOpenDashboardPath } from "@/lib/staffAccess";
import CassaApkOnly from "@/pages/pos/CassaApkOnly";
import ReminderSettings from "@/pages/client/ReminderSettings";
import AdminReleaseNotes from "@/pages/admin/AdminReleaseNotes";
import AdminMessages from "@/pages/admin/AdminMessages";
import Messages from "@/pages/client/Messages";
import AdminQuoteRequests from "@/pages/admin/AdminQuoteRequests";
import SuperAdmin from "@/pages/SuperAdmin";
import QuoteWizard from "@/pages/QuoteWizard";
import ResellerPanel from "@/pages/reseller/ResellerPanel";
import Hotel from "@/pages/client/Hotel";
import Appointments from "@/pages/client/Appointments";
import AppointmentsBeauty from "@/pages/client/AppointmentsBeauty";
import RestaurantReservations from "@/pages/client/RestaurantReservations";
import ReportToelettatura from "@/pages/client/ReportToelettatura";
import SystemInfo from "@/pages/client/SystemInfo";
import GelateriaConfig from "@/pages/client/GelateriaConfig";
import ErrorBoundary from "@/components/ErrorBoundary";
import ImpersonationBanner from "@/components/ImpersonationBanner";

function Protected({ children, role, perm, cassaApk }) {
  const { user, loading } = useAuth() || {};
  if (loading) return <div className="min-h-screen flex items-center justify-center text-velta-mute">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isClientUser = ["client_owner", "client_staff", "client"].includes(user?.role)
    || !!user?.is_demo
    || String(user?.email || "").toLowerCase() === "iovaneli.private@gmail.com"
    || String(user?.email || "").toLowerCase() === "demo@veltasistemi.it";
  if (role === "admin" && user?.role !== "superadmin") return <Navigate to={isClientUser ? "/dashboard" : "/login"} replace />;
  if (role === "reseller" && user?.role !== "reseller") return <Navigate to="/dashboard" replace />;
  if (role === "client" && !isClientUser) return <Navigate to={user?.role === "reseller" ? "/rivenditore" : "/admin"} replace />;
  if (perm && user?.role === "client_staff") {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    const required = Array.isArray(perm) ? perm : [perm];
    if (!required.some((p) => perms.includes(p))) {
      return <Navigate to={staffHomePath(user)} replace />;
    }
  }
  if (cassaApk && user?.role === "client_staff") {
    const perms = Array.isArray(user?.permissions) ? user.permissions : [];
    if (!perms.includes("cassa")) return <Navigate to={staffHomePath(user)} replace />;
    if (!isVeltaApk()) return <CassaApkOnly />;
  }
  return children;
}

function AppRoutes({ veltaMode }) {
  const loc = useLocation();
  return (
    <ErrorBoundary key={loc.pathname}>
      <Routes>
          <Route path="/" element={
            veltaMode === "palmare" ? <Navigate to="/palmare" replace />
            : veltaMode === "cassa" ? <Navigate to="/cassa" replace />
            : <Landing />
          } />
          <Route path="/login" element={<Login />} />
          <Route path="/attiva" element={<Activate />} />
          <Route path="/preventivo" element={<QuoteWizard />} />

          {/* Pagina pubblica fedeltà (accessibile via QR sullo scontrino, NO auth) */}
          <Route path="/carta/:tenantId/:customerId" element={<PublicLoyaltyCard />} />

          <Route path="/rivenditore" element={<Protected role="reseller"><ResellerPanel /></Protected>} />
          <Route path="/admin" element={<Protected role="admin"><AdminLayout /></Protected>}>
            <Route index element={<AdminHome />} />
            <Route path="clienti" element={<AdminClients />} />
            <Route path="superadmin" element={<SuperAdmin />} />
            <Route path="licenze" element={<Navigate to="/admin" replace />} />
            <Route path="note-rilascio" element={<AdminReleaseNotes />} />
            <Route path="messaggi" element={<AdminMessages />} />
            <Route path="richieste" element={<AdminQuoteRequests />} />
          </Route>

          <Route path="/dashboard" element={<Protected role="client" perm={["gestionale", "appuntamenti", "magazzino", "statistiche", "fatturazione", "stampanti", "branding", "utenti", "esportazioni"]}><DashboardLayout /></Protected>}>
            <Route index element={<Overview />} />
            <Route path="conti-aperti" element={<OpenOrders />} />
            <Route path="statistiche" element={<Stats />} />
            <Route path="articoli" element={<Products />} />
            <Route path="punti-vendita" element={<Stores />} />
            <Route path="stampanti" element={<Printers />} />
            <Route path="tavoli" element={<RequireMode any={["ristorante", "bar", "pizzeria", "festa"]}><Tables /></RequireMode>} />
            <Route path="magazzino" element={<RequireFeature name="warehouse"><Warehouse /></RequireFeature>} />
            <Route path="utenti" element={<Staff />} />
            <Route path="fatturazione" element={<Invoicing />} />
            <Route path="esportazioni" element={<Exports />} />
            <Route path="branding" element={<Branding />} />
            <Route path="report-operatori" element={<ReportOperators />} />
            <Route path="storni" element={<Refunds />} />
            <Route path="fedelta" element={<Loyalty />} />
            <Route path="alberghiero" element={<RequireFeature name="hotel_module"><Hotel /></RequireFeature>} />
            <Route path="abbonamento" element={<Billing />} />
            <Route path="chiusure" element={<Closures />} />
            <Route path="movimenti-gestionali" element={<GestionaleReport />} />
            <Route path="documenti" element={<DocumentsDay />} />
            <Route path="vendite" element={<SalesAnalytics />} />
            <Route path="appuntamenti" element={<RequireMode any={["toelettatura", "negozio_animali"]}><Appointments /></RequireMode>} />
            <Route path="appuntamenti-estetica" element={<RequireMode mode="parrucchiere"><AppointmentsBeauty /></RequireMode>} />
            <Route path="prenotazioni" element={<RequireMode any={["ristorante", "pizzeria", "bar"]}><RestaurantReservations /></RequireMode>} />
            <Route path="reminder" element={<RequireFeature name="whatsapp_reminders"><ReminderSettings /></RequireFeature>} />
            <Route path="report-toelettatura" element={<RequireMode mode="toelettatura"><ReportToelettatura /></RequireMode>} />
            <Route path="gelateria" element={<RequireMode mode="gelateria"><GelateriaConfig /></RequireMode>} />
            <Route path="sistema" element={<SystemInfo />} />
            <Route path="messaggi" element={<Messages />} />
          </Route>

          <Route path="/palmare" element={<Protected role="client" perm="palmare"><RequireFeature name="palmare"><Palmare /></RequireFeature></Protected>} />

          <Route path="/cassa" element={<Protected role="client" perm="cassa" cassaApk><ModeSelector /></Protected>} />
          <Route path="/cassa/ristorante" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="ristorante"><ErrorBoundary><POSRestaurant /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/festa" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="festa"><ErrorBoundary><POSFesta /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/negozio" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="negozio"><ErrorBoundary><POSNegozio /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/lavanderia" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="lavanderia"><ErrorBoundary><POSLavanderia /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/pizzeria" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="pizzeria"><ErrorBoundary><POSPizzeria /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/parrucchiere" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="parrucchiere"><ErrorBoundary><POSParrucchiere /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/alimentari" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="alimentari"><ErrorBoundary><POSPesati /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/macelleria" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="macelleria"><ErrorBoundary><POSPesati /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/panetteria" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="panetteria"><ErrorBoundary><POSPesati /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/tabacchi" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="tabacchi"><ErrorBoundary><POSRapido /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/edicola" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="edicola"><ErrorBoundary><POSRapido /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/enoteca" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="enoteca"><ErrorBoundary><POSRapido /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/bar" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="bar"><ErrorBoundary><POSRapido /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/gelateria" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="gelateria"><ErrorBoundary><POSGelateria /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/autolavaggio" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="autolavaggio"><ErrorBoundary><POSAutolavaggio /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/bnb" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="bnb"><ErrorBoundary><POSBnb /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/alloggio-dedicato" element={<Protected role="client"><Navigate to="/dashboard/alberghiero?tab=guests" replace /></Protected>} />
          <Route path="/cassa/toelettatura" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="toelettatura"><ErrorBoundary><POSToelettatura /></ErrorBoundary></RequireMode></Protected>} />
          <Route path="/cassa/negozio_animali" element={<Protected role="client" perm="cassa" cassaApk><RequireMode mode="negozio_animali"><ErrorBoundary><POSNegozio mode="negozio_animali" title="Velta One · Negozio Animali" /></ErrorBoundary></RequireMode></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}

function App() {
  const envMode = process.env.REACT_APP_VELTA_MODE || "full";
  const deviceRole = getDeviceRole();
  const veltaMode = deviceRole === "cassa" || deviceRole === "palmare" ? deviceRole : envMode;
  const [splash, setSplash] = useState(true);

  useEffect(() => { startOfflineSync(); }, []);

  return (
    <AuthProvider>
      <FeaturesProvider>
        <StoreProvider>
        <BrowserRouter>
        {splash && <SplashScreen onDone={() => setSplash(false)} />}
        <Toaster richColors position="top-right" />
        <OfflinePendingBar />
        <ImpersonationBanner />
        <UpdateBanner />
        <AutoAppUpdate />
        <WhatsNew />
        <DemoBadge />
        <AppRoutes veltaMode={veltaMode} />
      </BrowserRouter>
        </StoreProvider>
      </FeaturesProvider>
    </AuthProvider>
  );
}

export default App;
